import useUrl from './helpers/useUrl'

/** @param {import("@nuxtjs/axios").NuxtAxiosInstance} client */
export default (client, { apiUrl } = {}) => {
  const root = useUrl(`${apiUrl || ''}/billboard`)

  return {
    /**
     * @param {Object} GetBannerBillboardDTO
     * @param {string} GetBannerBillboardDTO.term
     *
     * @returns {Promise<AxiosResponse<GetBannerBillboardResponse>>}
     * */
    getBannerBillboard({ term }) {
      const url = root(term)
      return client.get(url)
    },

    /**
     * @param {Object} GetBannersDTO
     * @param {string} GetBannersDTO.type
     *
     * @returns {Promise<AxiosResponse<GetBannersResponse>>}
     * */
    getBanners({ type, is_external, is_active }) {
      const url = root('')
      const params = {
        type,
        is_external,
        is_active,
      }
      return client.get(url, { params })
    },
  }
}

/** @typedef {object} GetBannerBillboardResponse
 * @property {object} banner
 * @property {number} banner.id
 * @property {string} banner.term
 * @property {string} banner.publication_start_date
 * @property {string} banner.publication_end_date
 * @property {string} banner.access_level
 * @property {number} banner.order_index
 * @property {number} banner.is_external
 * @property {string} banner.external_url
 * @property {number} banner.is_active
 * @property {number} banner.is_deleted
 * @property {number} banner.is_hide
 * @property {number} banner.is_offline
 * @property {number} banner.is_online
 * @property {number} banner.is_main_page
 * @property {boolean} banner.is_show_timer
 * @property {string} banner.created_date
 * @property {string} banner.updated_date
 * @property {null} banner.segments
 * @property {object} banner.ru
 * @property {string} banner.ru.title
 * @property {string} banner.ru.description_short
 * @property {null} banner.ru.description
 * @property {null} banner.ru.prize
 * @property {string} banner.ru.background_image
 * @property {string} banner.ru.background_image_mobile
 * @property {string} banner.ru.main_page_image
 * @property {object} banner.ru.button
 * @property {null} banner.ru.button.url
 * @property {null} banner.ru.button.title
 * @property {string} banner.ru.description_short_app
 * @property {null} banner.ru.description_app
 * @property {string} banner.ru.text_app
 * @property {string} banner.ru.text
 * @property {object} banner.ua
 * @property {string} banner.ua.title
 * @property {string} banner.ua.description_short
 * @property {null} banner.ua.description
 * @property {null} banner.ua.prize
 * @property {string} banner.ua.background_image
 * @property {string} banner.ua.background_image_mobile
 * @property {string} banner.ua.main_page_image
 * @property {object} banner.ua.button
 * @property {null} banner.ua.button.url
 * @property {null} banner.ua.button.title
 * @property {string} banner.ua.description_short_app
 * @property {string} banner.ua.description_app
 * @property {string} banner.ua.text_app
 * @property {string} banner.ua.text
 * @property {object[]} banner.extra_banners
 * @property {number} banner.extra_banners.id
 * @property {string} banner.extra_banners.term
 * @property {string} banner.extra_banners.publication_start_date
 * @property {string|null} banner.extra_banners.publication_end_date
 * @property {string} banner.extra_banners.access_level
 * @property {number} banner.extra_banners.order_index
 * @property {number} banner.extra_banners.is_external
 * @property {null|string} banner.extra_banners.external_url
 * @property {number} banner.extra_banners.is_active
 * @property {number} banner.extra_banners.is_deleted
 * @property {number} banner.extra_banners.is_hide
 * @property {number} banner.extra_banners.is_offline
 * @property {number} banner.extra_banners.is_online
 * @property {number} banner.extra_banners.is_main_page
 * @property {boolean} banner.extra_banners.is_show_timer
 * @property {string} banner.extra_banners.created_date
 * @property {string} banner.extra_banners.updated_date
 * @property {null} banner.extra_banners.segments
 * @property {object} banner.extra_banners.ru
 * @property {string} banner.extra_banners.ru.title
 * @property {string} banner.extra_banners.ru.description_short
 * @property {string|null} banner.extra_banners.ru.description
 * @property {null} banner.extra_banners.ru.prize
 * @property {string} banner.extra_banners.ru.background_image
 * @property {string} banner.extra_banners.ru.background_image_mobile
 * @property {string} banner.extra_banners.ru.main_page_image
 * @property {object} banner.extra_banners.ru.button
 * @property {null} banner.extra_banners.ru.button.url
 * @property {null} banner.extra_banners.ru.button.title
 * @property {string} banner.extra_banners.ru.description_short_app
 * @property {string|null} banner.extra_banners.ru.description_app
 * @property {string} banner.extra_banners.ru.text_app
 * @property {string} banner.extra_banners.ru.text
 * @property {object} banner.extra_banners.ua
 * @property {string} banner.extra_banners.ua.title
 * @property {string} banner.extra_banners.ua.description_short
 * @property {string|null} banner.extra_banners.ua.description
 * @property {null} banner.extra_banners.ua.prize
 * @property {string} banner.extra_banners.ua.background_image
 * @property {string} banner.extra_banners.ua.background_image_mobile
 * @property {string} banner.extra_banners.ua.main_page_image
 * @property {object} banner.extra_banners.ua.button
 * @property {null} banner.extra_banners.ua.button.url
 * @property {null} banner.extra_banners.ua.button.title
 * @property {string} banner.extra_banners.ua.description_short_app
 * @property {string|null} banner.extra_banners.ua.description_app
 * @property {string} banner.extra_banners.ua.text_app
 * @property {string} banner.extra_banners.ua.text
 */

/** @typedef {object} GetBannersResponse
 * @property {object[]} banners
 * @property {number} banners.id
 * @property {string} banners.term
 * @property {string|null} banners.publication_start_date
 * @property {string|null} banners.publication_end_date
 * @property {string} banners.access_level
 * @property {number} banners.order_index
 * @property {number} banners.is_external
 * @property {string|null} banners.external_url
 * @property {number} banners.is_active
 * @property {number} banners.is_deleted
 * @property {number} banners.is_hide
 * @property {number} banners.is_offline
 * @property {number} banners.is_online
 * @property {number} banners.is_main_page
 * @property {boolean} banners.is_show_timer
 * @property {string} banners.created_date
 * @property {string} banners.updated_date
 * @property {null} banners.segments
 * @property {object} banners.ru
 * @property {string} banners.ru.title
 * @property {string} banners.ru.description_short
 * @property {null|string} banners.ru.description
 * @property {null} banners.ru.prize
 * @property {string} banners.ru.background_image
 * @property {string} banners.ru.background_image_mobile
 * @property {string} banners.ru.main_page_image
 * @property {object} banners.ru.button
 * @property {null} banners.ru.button.url
 * @property {null} banners.ru.button.title
 * @property {string|null} banners.ru.description_short_app
 * @property {null|string} banners.ru.description_app
 * @property {string} banners.ru.text_app
 * @property {string} banners.ru.text
 * @property {object} banners.ua
 * @property {string} banners.ua.title
 * @property {string} banners.ua.description_short
 * @property {null|string} banners.ua.description
 * @property {null} banners.ua.prize
 * @property {string} banners.ua.background_image
 * @property {string} banners.ua.background_image_mobile
 * @property {string} banners.ua.main_page_image
 * @property {object} banners.ua.button
 * @property {null} banners.ua.button.url
 * @property {null} banners.ua.button.title
 * @property {string|null} banners.ua.description_short_app
 * @property {string|null} banners.ua.description_app
 * @property {string} banners.ua.text_app
 * @property {string} banners.ua.text
 * @property {object} banners.relativeDates
 * @property {number} banners.relativeDates.publicationStartsIn
 * @property {number} banners.relativeDates.publicationEndsIn
 */
